/**
 * Lead Document Service — CRUD for documents in the lead documents area (any file type uploads).
 */

import { db, storage } from './firebase';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import type { LeadDocument } from '../types';
import { sanitizeFileNameForStoragePath } from './storageFileNames';
import { ensureCrmStorageAccessBeforeUpload } from './crmStorageAccess';

const COLLECTION = 'leadDocuments';
const STORAGE_PREFIX = 'leadDocuments';

/** Check if a mimeType is an image */
export function isImageType(mimeType?: string): boolean {
  return !!mimeType && mimeType.startsWith('image/');
}

/** Check if a mimeType is a PDF */
export function isPdfType(mimeType?: string): boolean {
  return mimeType === 'application/pdf';
}

function fromFirestore(docSnap: { id: string; data: () => Record<string, unknown> }): LeadDocument {
  const d = docSnap.data();
  return {
    id: docSnap.id,
    leadId: d.leadId as string,
    dealId: (d.dealId as string) || undefined,
    name: d.name as string,
    url: d.url as string,
    storagePath: d.storagePath as string | undefined,
    mimeType: (d.mimeType as string | undefined) || 'application/pdf',
    size: d.size as number,
    uploadedAt: (d.uploadedAt as { toDate: () => Date })?.toDate?.() ?? new Date(d.uploadedAt as string),
    uploadedBy: d.uploadedBy as string,
  };
}

/** Upload a file and create a lead document record */
export async function uploadLeadDocument(
  leadId: string,
  file: File,
  uploadedBy: string,
  dealId?: string,
): Promise<LeadDocument> {
  const storagePath = `${STORAGE_PREFIX}/${leadId}/${Date.now()}-${sanitizeFileNameForStoragePath(file.name)}`;
  const storageRef = ref(storage, storagePath);
  await ensureCrmStorageAccessBeforeUpload();
  await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
  const url = await getDownloadURL(storageRef);

  const docData: Record<string, unknown> = {
    leadId,
    name: file.name,
    url,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    uploadedAt: Timestamp.now(),
    uploadedBy,
  };
  if (dealId) docData.dealId = dealId;

  const docRef = await addDoc(collection(db, COLLECTION), docData);

  return {
    id: docRef.id,
    leadId,
    dealId,
    name: file.name,
    url,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    uploadedAt: new Date(),
    uploadedBy,
  };
}

/** Same as uploadLeadDocument but reports byte-level progress (resumable Storage upload). */
export async function uploadLeadDocumentWithProgress(
  leadId: string,
  file: File,
  uploadedBy: string,
  dealId: string | undefined,
  onProgress: (bytesTransferred: number, totalBytes: number) => void,
): Promise<LeadDocument> {
  const storagePath = `${STORAGE_PREFIX}/${leadId}/${Date.now()}-${sanitizeFileNameForStoragePath(file.name)}`;
  const storageRef = ref(storage, storagePath);
  await ensureCrmStorageAccessBeforeUpload();
  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type || 'application/octet-stream',
  });

  await new Promise<void>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      snapshot => {
        onProgress(snapshot.bytesTransferred, snapshot.totalBytes);
      },
      reject,
      () => resolve(),
    );
  });

  const url = await getDownloadURL(uploadTask.snapshot.ref);

  const docData: Record<string, unknown> = {
    leadId,
    name: file.name,
    url,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    uploadedAt: Timestamp.now(),
    uploadedBy,
  };
  if (dealId) docData.dealId = dealId;

  const docRef = await addDoc(collection(db, COLLECTION), docData);

  return {
    id: docRef.id,
    leadId,
    dealId,
    name: file.name,
    url,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    uploadedAt: new Date(),
    uploadedBy,
  };
}

/** Get all documents for a lead */
export async function getLeadDocuments(leadId: string): Promise<LeadDocument[]> {
  const q = query(collection(db, COLLECTION), where('leadId', '==', leadId));
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(d => fromFirestore({ id: d.id, data: () => d.data() }))
    .sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
}

/** Update the dealId (project assignment) of a lead document */
export async function updateLeadDocumentDealId(documentId: string, dealId: string | null): Promise<void> {
  const docRef = doc(db, COLLECTION, documentId);
  if (dealId) {
    await updateDoc(docRef, { dealId });
  } else {
    // Remove the field by setting to FieldValue.delete — but simpler: set empty string and handle in query
    // Actually, Firestore updateDoc can accept deleteField()
    const { deleteField } = await import('firebase/firestore');
    await updateDoc(docRef, { dealId: deleteField() });
  }
}

/** Find a lead document by its public download URL (for legacy project-file mirrors). */
export async function findLeadDocumentByUrl(url: string): Promise<LeadDocument | null> {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;
  const q = query(collection(db, COLLECTION), where('url', '==', trimmed));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return fromFirestore({ id: snapshot.docs[0].id, data: () => snapshot.docs[0].data() });
}

/** Delete a lead document and its storage file */
export async function deleteLeadDocument(documentId: string, storagePath?: string): Promise<void> {
  if (storagePath) {
    try {
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
    } catch {
      // Ignore if file doesn't exist
    }
  }
  await deleteDoc(doc(db, COLLECTION, documentId));
}
